import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { AspopResult, aspopUrl, ripeUrl } from '../../utils/apnic';
import { formatCompact, formatInt } from '../../utils/format';
import { DataBadge } from '../DataBadge';
import { SourceRef } from '../SourceRef';
import { PageSources } from '../PageSources';
interface TopNetworksProps {
  cca2: string;
  countryName: string;
  population: number;
  result: AspopResult | null;
}
export function TopNetworks({
  cca2,
  countryName,
  population,
  result
}: TopNetworksProps) {
  const rows = result?.rows ?? [];
  const max = Math.max(1, ...rows.map(r => r.users));
  const src = aspopUrl(cca2);
  return <section aria-labelledby="networks-title" className="mt-12" data-id="element-319" data-mp="28nhab3hxkd95">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-320" data-mp="2dlagvxn5b99y">
        <h2 id="networks-title" className="font-serif text-2xl text-ink" data-id="element-321" data-mp="3k1cskl44t24l">
          Top networks
          <SourceRef id="apnicAspop" href={src} data-id="element-322" data-mp="3oyv3ui5mftfr" />
        </h2>
      </div>
      <PageSources data-id="element-323" data-mp="2hc697lj7us7a">
        <DataBadge mode={result ? result.mode : 'loading'} source="APNIC aspop · top networks" asOf={result?.asOf} data-id="element-324" data-mp="2hcof79vokkse" />
      </PageSources>
      <p className="mt-1 text-[13px] text-muted" data-id="element-325" data-mp="2db2u518zt074">
        The autonomous systems that carry {countryName}’s internet users, by APNIC’s ad-based user estimate.
        {result?.mode === 'snapshot' && ' Showing the top 5 from the latest snapshot.'}
      </p>

      {!result && <p className="mt-6 py-8 text-center font-mono text-xs text-muted" role="status" data-id="element-326" data-mp="1yec43il7l0u6">
          Fetching APNIC table…
        </p>}

      {result && rows.length === 0 && <p className="mt-6 rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-muted" data-id="element-327" data-mp="1pkgw82spcwau">
          No live table could be read and no embedded snapshot exists for this country — no figures are shown rather than guessed.
        </p>}

      {rows.length > 0 && <>
          <div className="mt-5 hidden grid-cols-[2rem_minmax(0,1fr)_6.5rem_6rem_6rem] gap-x-4 border-b border-line pb-2 font-mono text-[10.5px] uppercase tracking-wider text-muted md:grid" data-id="element-328" data-mp="1ang7871wbfg2">
            <span data-id="element-329" data-mp="15zq9d3tzrxn6">#</span>
            <span data-id="element-330" data-mp="15zzxelvh7uq8">Operator · ASN</span>
            <span className="text-right" data-id="element-331" data-mp="15wuandabl4xo">Est. users</span>
            <span className="text-right" data-id="element-332" data-mp="s0qu2msdcv3o">% of pop.</span>
            <span className="text-right" data-id="element-333" data-mp="w0r8w8jdr8b9">% global</span>
          </div>
          <ol className="divide-y divide-line/60" data-id="element-334" data-mp="w33o4dxr74km">
            {rows.map(r => <li key={r.asn} className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 py-3 md:grid-cols-[2rem_minmax(0,1fr)_6.5rem_6rem_6rem]" data-id="element-335" data-mp="1jr50jxy1z55b">
                <span className="font-mono text-[12px] text-muted" data-id="element-336" data-mp="1ax8juynnqkxa">{r.rank}</span>
                <div className="min-w-0" data-id="element-337" data-mp="1ezd8fiz90rd2">
                  <p className="flex items-baseline gap-2 truncate text-[13.5px] text-ink" data-id="element-338" data-mp="1eybv17urfss6">
                    <span className="truncate" data-id="element-339" data-mp="22nwju7dlhk9d">{r.name}</span>
                    <span className="shrink-0 font-mono text-[11px] text-human" data-id="element-340" data-mp="27gppqyksuyn1">{r.asn}</span>
                  </p>
                  <div className="mt-1.5 h-1.5 rounded-full bg-raised" aria-hidden="true" data-id="element-341" data-mp="3s5jnie2ho5ah">
                    <div className="h-full rounded-full bg-human/75" style={{
                width: `${r.users / max * 100}%`
              }} data-id="element-342" data-mp="3n89z91qu5zo8" />
                  </div>
                </div>
                <span className="text-right font-mono text-[13px] tabular text-ink" title={`${formatInt(r.users)} users`} data-id="element-343" data-mp="5suyc7vtj4dw">
                  {formatCompact(r.users)}
                  <SourceRef id="apnicAspop" href={src} estimate={{
              low: formatCompact(r.users * 0.85),
              high: formatCompact(r.users * 1.15)
            }} note="APNIC user counts are estimates from ad-measurement sampling." data-id="element-344" data-mp="3988ydmakms8r" />
                </span>
                <span className="col-start-2 font-mono text-[12px] tabular text-muted md:col-start-auto md:text-right" data-id="element-345" data-mp="3e6tp8v3xnzkj">
                  <span className="md:hidden" data-id="element-346" data-mp="yf64rz63vzim">pop. </span>
                  {population > 0 ? `${(r.users / population * 100).toFixed(1)}%` : '—'}
                </span>
                <span className="font-mono text-[12px] tabular text-muted md:text-right" data-id="element-347" data-mp="tixhtzlo320w">
                  <span className="md:hidden" data-id="element-348" data-mp="2z6y6dcqp7n5f">global </span>
                  {r.pctInternet != null ? `${r.pctInternet.toFixed(2)}%` : '—'}
                </span>
              </li>)}
          </ol>
        </>}

      <a href={ripeUrl(cca2)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-[13px] text-muted hover:text-machine" data-id="element-349" data-mp="3d4ejf73i2f65">
        Full ASN registry →
        <span className="sr-only" data-id="element-350" data-mp="3n2khfdm6r15q">(RIPEstat, opens in new tab)</span>
        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-351" data-mp="3n0puxuv9n0zj" />
      </a>
      <SourceRef id="ripestat" href={ripeUrl(cca2)} data-id="element-352" data-mp="3ruu1we4j790m" />
    </section>;
}